import React, { useState } from 'react';
import {
  Box, Container, Grid, Typography, Card, CardContent,
  Stack, Chip, Paper,
  Fade, Zoom, alpha
} from '@mui/material';
import { styled, keyframes } from '@mui/material/styles';
import {
  Science, Biotech, AutoFixHigh, Speed,
  Psychology, Security, Cloud, CheckCircle,
  RocketLaunch, SupportAgent,
  Email, Phone,
  Shield,
  Science as Microscope,
  Biotech as DNA,
  Analytics as Insights,
} from '@mui/icons-material';
import HomeFooter from '../HomeFooter';
import Navbar from '../Navbar.jsx';

// Professional color palette (Same as other pages)
const colors = {
  primary: '#4551be', // Exact blue
  secondary: '#6b77d4', // Lighter blue accent
  success: '#10b981', // Green
  warning: '#f59e0b', // Orange
  dark: '#1e293b', // Dark text
  light: '#f0f4f8', // Light background
  gray: '#64748b', // Slate 500
  accent: '#4551be',
  textSecondary: '#64748b', // Dark grey for secondary text
  gradient: 'linear-gradient(135deg, #4551be 0%, #6b77d4 100%)'
};

// Animations
const float = keyframes`
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-20px); }
`;

const ServiceCard = styled(Card)(({ theme }) => ({
  height: '100%',
  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
  '&:hover': {
    transform: 'translateY(-8px)',
    boxShadow: `0 20px 40px -12px ${alpha(colors.primary, 0.2)}`
  }
}));

const FeatureItem = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1.5),
  marginBottom: theme.spacing(1.5)
}));

const RDTestingLab = () => {
  const [activeResearch, setActiveResearch] = useState('automation');

  // R&D Lab Research Areas
  const researchAreas = [
    {
      id: 'test-automation',
      title: 'Test Automation Research',
      icon: <AutoFixHigh />,
      description: 'Investigating novel approaches to test automation using AI and machine learning to reduce manual intervention and improve test coverage.',
      color: colors.primary,
      features: ['AI-driven Testing', 'Self-healing Scripts', 'Predictive Analytics']
    },
    {
      id: 'performance-lab',
      title: 'Performance Testing Lab',
      icon: <Speed />,
      description: 'Researching advanced performance testing methodologies for cloud-native applications and microservices architectures.',
      color: colors.secondary,
      features: ['Load Testing Research', 'Scalability Studies', 'Bottleneck Analysis']
    },
    {
      id: 'security-research',
      title: 'Security Testing R&D',
      icon: <Security />,
      description: 'Developing cutting-edge security testing frameworks to identify vulnerabilities in modern software systems.',
      color: colors.success,
      features: ['Penetration Research', 'Threat Modeling', 'Zero-day Detection']
    },
    {
      id: 'ai-testing',
      title: 'AI/ML Testing',
      icon: <Psychology />,
      description: 'Researching testing methodologies specifically designed for artificial intelligence and machine learning systems.',
      color: colors.warning,
      features: ['Model Validation', 'Bias Detection', 'Data Drift Analysis']
    },
    {
      id: 'devops-lab',
      title: 'DevOps Testing Lab',
      icon: <Cloud />,
      description: 'Investigating continuous testing practices integrated with CI/CD pipelines for faster, reliable deployments.',
      color: colors.primary,
      features: ['CI/CD Testing', 'Infrastructure Testing', 'Container Testing']
    },
    {
      id: 'experimental',
      title: 'Experimental Testing',
      icon: <Cloud />,
      description: 'Exploring experimental testing techniques and frameworks for emerging technologies and platforms.',
      color: colors.secondary,
      features: ['Prototype Validation', 'Concept Testing', 'Innovation Research']
    }
  ];

  // Why Choose Our R&D Lab
  const whyChooseUs = [
    {
      icon: <Science />,
      title: "Advanced Research",
      points: ["State-of-the-art testing lab", "Cutting-edge methodologies", "Published research papers"]
    },
    {
      icon: <Microscope />,
      title: "Expert Researchers",
      points: ["PhD-level researchers", "Industry veterans", "Certified testing experts"]
    },
    {
      icon: <RocketLaunch />,
      title: "Innovation Focus",
      points: ["Emerging tech research", "Patent-pending methods", "Industry collaborations"]
    },
    {
      icon: <SupportAgent />,
      title: "Research Support",
      points: ["24/7 lab access", "Research consultation", "Methodology transfer"]
    },
    {
      icon: <Shield />,
      title: "Quality Standards",
      points: ["ISO 17025 certified", "GLP compliant", "Peer-reviewed research"]
    },
    {
      icon: <Insights />,
      title: "Proven Impact",
      points: ["Industry adoption", "Research citations", "ROI validation"]
    }
  ];

  // R&D Lab Process
  const processSteps = [
    { step: "01", title: "Research Planning", description: "Define research objectives and hypotheses" },
    { step: "02", title: "Experimental Design", description: "Design controlled experiments and test protocols" },
    { step: "03", title: "Data Collection", description: "Execute experiments and collect research data" },
    { step: "04", title: "Analysis", description: "Analyze results and validate findings statistically" },
    { step: "05", title: "Peer Review", description: "Submit findings for peer review and validation" },
    { step: "06", title: "Publication", description: "Publish research and transfer to industry" }
  ];

  // Key Research Findings
  const findings = [
    {
      insight: 'AI-powered test automation reduces maintenance effort by 45% compared to traditional automation.',
      category: 'Automation Research'
    },
    {
      insight: 'Early performance testing in development cycle reduces production incidents by 60%.',
      category: 'Performance Lab'
    },
    {
      insight: 'Continuous testing in CI/CD pipelines accelerates release cycles by 35% without quality compromise.',
      category: 'DevOps Research'
    },
    {
      insight: 'Hybrid security testing approaches detect 40% more vulnerabilities than single-method testing.',
      category: 'Security Lab'
    }
  ];

  const stats = [
    { value: "50+", label: "Active Research Projects", icon: <Science /> },
    { value: "25+", label: "Published Papers", icon: <DNA /> },
    { value: "15+", label: "Research Patents", icon: <Biotech /> },
    { value: "24/7", label: "Lab Operations", icon: <SupportAgent /> }
  ];

  // Lab Equipment & Facilities
  const facilities = [
    "High-performance computing cluster",
    "Automated test execution grid",
    "Performance testing infrastructure",
    "Security testing sandbox environment",
    "AI/ML testing frameworks",
    "Cloud testing platforms"
  ];

  return (
    <Box sx={{ bgcolor: colors.light }}>
      <Navbar />

      {/* Hero Section */}
            <Box
  sx={{
    minHeight: { xs: 'auto', md: '60vh' },
    display: 'flex',
    alignItems: 'center',

    // Navbar overlap fix
   pt: { xs: 9, sm: 7, md: 8 },
    pb: { xs: 7, sm: 9, md: 12 },

    background: `linear-gradient(
      135deg,
      ${alpha(colors.primary, 0.03)} 0%,
      ${alpha(colors.secondary, 0.03)} 100%
    )`,

    position: 'relative',
    overflow: 'hidden',
    width: '100%',
  }}
>
        <Container maxWidth="xl">
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={6}>
              <Fade in timeout={1000}>
                <Box>
                  <Chip
                    icon={<Science />}
                    label="🔬 R&D TESTING LABORATORY"
                    sx={{
                      mb: 3,
                      mt: { xs: 2, md: 11 },
                      bgcolor: alpha(colors.primary, 0.1),
                      color: colors.primary,
                      fontWeight: 600,
                      border: `1px solid ${alpha(colors.primary, 0.2)}`
                    }}
                  />

                  <Typography variant="h1" sx={{
                    fontSize: { xs: '2.5rem', md: '3.5rem' },
                    fontWeight: 800,
                    lineHeight: 1.2,
                    mb: 3,
                    color: colors.dark // Dark text
                  }}>
                    Software Testing
                    <Box component="span" sx={{
                      display: 'block',
                      color: colors.primary // Blue text
                    }}>
                      Research & Development Lab
                    </Box>
                  </Typography>

                  <Typography sx={{
                    color: colors.textSecondary,
                    mb: 4,
                    fontSize: '1.2rem',
                    lineHeight: 1.7,
                    maxWidth: '90%'
                  }}>
                    Our R&D laboratory conducts pioneering research in software testing methodologies,
                    developing innovative approaches that shape the future of quality assurance. We bridge
                    the gap between academic research and industrial application.
                  </Typography>
                </Box>
              </Fade>
            </Grid>

            <Grid item xs={12} md={6}>
              <Zoom in timeout={1000} style={{ transitionDelay: '300ms' }}>
                <Box sx={{
                  animation: `${float} 6s ease-in-out infinite`,
                  textAlign: 'center',
                  position: 'relative'
                }}>
                  <Box sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: { xs: '280px', md: '380px' },
                    height: { xs: '280px', md: '380px' },
                    borderRadius: '30% 70% 70% 30% / 30% 30% 70% 70%',
                    background: `linear-gradient(135deg, ${alpha(colors.primary, 0.1)} 0%, ${alpha(colors.secondary, 0.1)} 100%)`,
                    margin: '0 auto',
                    position: 'relative',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: -15,
                      left: -15,
                      right: -15,
                      bottom: -15,
                      borderRadius: '40% 60% 60% 40% / 40% 40% 60% 60%',
                      background: `linear-gradient(135deg, ${alpha(colors.primary, 0.2)} 0%, ${alpha(colors.secondary, 0.2)} 100%)`,
                      zIndex: -1,
                      animation: `${float} 8s ease-in-out infinite reverse`
                    }
                  }}>
                    <Microscope sx={{
                      fontSize: { xs: 100, md: 150 },
                      color: colors.primary
                    }} />
                  </Box>

                  {/* Floating Elements */}
                  <Box sx={{
                    position: 'absolute',
                    top: '15%',
                    right: '10%',
                    width: 60,
                    height: 60,
                    borderRadius: '15px',
                    background: alpha(colors.success, 0.2),
                    display: { xs: 'none', md: 'flex' },
                    alignItems: 'center',
                    justifyContent: 'center',
                    animation: `${float} 7s ease-in-out infinite`,
                    animationDelay: '1s'
                  }}>
                    <Biotech sx={{ color: colors.success }} />
                  </Box>

                  <Box sx={{
                    position: 'absolute',
                    bottom: '20%',
                    left: '5%',
                    width: 50,
                    height: 50,
                    borderRadius: '10px',
                    background: alpha(colors.warning, 0.2),
                    display: { xs: 'none', md: 'flex' },
                    alignItems: 'center',
                    justifyContent: 'center',
                    animation: `${float} 8s ease-in-out infinite`,
                    animationDelay: '2s'
                  }}>
                    <DNA sx={{ color: colors.warning }} />
                  </Box>

                  <Box sx={{
                    position: 'absolute',
                    top: '30%',
                    left: '15%',
                    width: 45,
                    height: 45,
                    borderRadius: '50%',
                    background: alpha(colors.secondary, 0.2),
                    display: { xs: 'none', md: 'flex' },
                    alignItems: 'center',
                    justifyContent: 'center',
                    animation: `${float} 9s ease-in-out infinite`,
                    animationDelay: '1.5s'
                  }}>
                    <Cloud sx={{ color: colors.secondary, fontSize: 25 }} />
                  </Box>
                </Box>
              </Zoom>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Why Choose Our R&D Lab */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Box textAlign="center" mb={6}>
          <Chip
            label="WHY OUR R&D LAB"
            sx={{
              bgcolor: alpha(colors.primary, 0.1),
              color: colors.primary,
              fontWeight: 600,
              mb: 2
            }}
          />
          <Typography variant="h2" sx={{ fontWeight: 700, mb: 2, color: colors.dark }}>
            Why Industry Leaders Partner With Our Lab
          </Typography>
          <Typography variant="h5" sx={{ maxWidth: 700, mx: 'auto', color: colors.textSecondary }}>
            We deliver breakthrough research that transforms software testing practices
          </Typography>
        </Box>

        <Grid container spacing={3}>
          {whyChooseUs.map((item, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <Paper sx={{
                p: 3,
                height: '100%',
                transition: 'transform 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: `0 12px 24px ${alpha(colors.primary, 0.15)}`
                }
              }}>
                <Box sx={{
                  width: 48,
                  height: 48,
                  borderRadius: 2,
                  background: colors.gradient,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mb: 2,
                  color: 'white'
                }}>
                  {item.icon}
                </Box>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 700, color: colors.dark }}>
                  {item.title}
                </Typography>
                <Stack spacing={1}>
                  {item.points.map((point, idx) => (
                    <FeatureItem key={idx}>
                      <CheckCircle sx={{ fontSize: 16, color: colors.success }} />
                      <Typography variant="body2" sx={{ color: colors.textSecondary }}>
                        {point}
                      </Typography>
                    </FeatureItem>
                  ))}
                </Stack>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Lab Facilities */}
      <Box sx={{ bgcolor: 'white', py: 8 }}>
        <Container maxWidth="lg">
          <Box textAlign="center" mb={6}>
            <Chip
              label="LAB FACILITIES"
              sx={{
                bgcolor: alpha(colors.primary, 0.1),
                color: colors.primary,
                fontWeight: 600,
                mb: 2
              }}
            />
            <Typography variant="h2" sx={{ fontWeight: 700, mb: 2, color: colors.dark }}>
              State-of-the-Art Research Infrastructure
            </Typography>
          </Box>

          <Grid container spacing={2}>
            {facilities.map((facility, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Paper sx={{
                  p: 2,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  transition: 'transform 0.3s ease',
                  '&:hover': {
                    transform: 'translateX(4px)',
                    borderColor: colors.primary,
                    boxShadow: `0 4px 12px ${alpha(colors.primary, 0.1)}`
                  }
                }}>
                  <CheckCircle sx={{ color: colors.success }} />
                  <Typography sx={{ color: colors.dark }}>{facility}</Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Research Process */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Box textAlign="center" mb={6}>
          <Chip
            label="RESEARCH PROCESS"
            sx={{
              bgcolor: alpha(colors.primary, 0.1),
              color: colors.primary,
              fontWeight: 600,
              mb: 2
            }}
          />
          <Typography variant="h2" sx={{ fontWeight: 700, mb: 2, color: colors.dark }}>
            Our Research Methodology
          </Typography>
          <Typography variant="h5" sx={{ maxWidth: 700, mx: 'auto', color: colors.textSecondary }}>
            Rigorous scientific approach to testing research and development
          </Typography>
        </Box>

        <Grid container spacing={3}>
          {processSteps.map((item, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <Paper sx={{
                p: 3,
                height: '100%',
                position: 'relative',
                transition: 'transform 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: `0 12px 24px ${alpha(colors.primary, 0.15)}`
                }
              }}>
                <Typography variant="h2" sx={{
                  position: 'absolute',
                  top: 10,
                  right: 20,
                  fontSize: '4rem',
                  fontWeight: 900,
                  color: alpha(colors.primary, 0.1),
                  lineHeight: 1
                }}>
                  {item.step}
                </Typography>
                <Box sx={{ position: 'relative', zIndex: 1 }}>
                  <Typography variant="h5" gutterBottom sx={{ fontWeight: 700, color: colors.primary }}>
                    {item.title}
                  </Typography>
                  <Typography sx={{ color: colors.textSecondary }}>
                    {item.description}
                  </Typography>
                </Box>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Research Areas */}
      <Box sx={{ bgcolor: 'white', py: 8 }}>
        <Container maxWidth="lg">
          <Box textAlign="center" mb={6}>
            <Chip
              label="RESEARCH AREAS"
              sx={{
                bgcolor: alpha(colors.primary, 0.1),
                color: colors.primary,
                fontWeight: 600,
                mb: 2
              }}
            />
            <Typography variant="h2" sx={{ fontWeight: 700, mb: 2, color: colors.dark }}>
              Current Research Programs
            </Typography>
          </Box>

          <Grid container spacing={3}>
            {researchAreas.map((area, index) => (
              <Grid item xs={12} sm={6} md={4} key={area.id}>
                <ServiceCard
                  onMouseEnter={() => setActiveResearch(area.id)}
                  onMouseLeave={() => setActiveResearch(null)}
                >
                  <CardContent sx={{ p: 3 }}>
                    <Box sx={{
                      width: 50,
                      height: 50,
                      borderRadius: 2,
                      bgcolor: alpha(area.color, 0.1),
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mb: 2,
                      color: area.color
                    }}>
                      {area.icon}
                    </Box>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, color: colors.dark }}>
                      {area.title}
                    </Typography>
                    <Typography variant="body2" paragraph sx={{ color: colors.textSecondary }}>
                      {area.description}
                    </Typography>
                    <Stack spacing={1}>
                      {area.features.map((feature, idx) => (
                        <FeatureItem key={idx}>
                          <CheckCircle sx={{ fontSize: 14, color: colors.success }} />
                          <Typography variant="caption" sx={{ color: colors.dark }}>{feature}</Typography>
                        </FeatureItem>
                      ))}
                    </Stack>
                  </CardContent>
                </ServiceCard>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Key Research Findings */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Box textAlign="center" mb={6}>
          <Chip
            label="RESEARCH FINDINGS"
            sx={{
              bgcolor: alpha(colors.primary, 0.1),
              color: colors.primary,
              fontWeight: 600,
              mb: 2
            }}
          />
          <Typography variant="h2" sx={{ fontWeight: 700, mb: 2, color: colors.dark }}>
            Breakthrough Discoveries
          </Typography>
        </Box>

        <Grid container spacing={3}>
          {findings.map((finding, index) => (
            <Grid item xs={12} md={6} key={index}>
              <Paper sx={{
                p: 3,
                borderLeft: `4px solid ${colors.primary}`,
                transition: 'transform 0.3s ease',
                '&:hover': {
                  transform: 'translateX(4px)',
                  boxShadow: `0 8px 16px ${alpha(colors.primary, 0.1)}`
                }
              }}>
                <Typography variant="body1" paragraph sx={{ fontWeight: 500, color: colors.dark }}>
                  "{finding.insight}"
                </Typography>
                <Chip
                  label={finding.category}
                  size="small"
                  sx={{
                    bgcolor: alpha(colors.primary, 0.1),
                    color: colors.primary,
                    fontWeight: 600
                  }}
                />
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Lab Stats */}
      <Box sx={{ bgcolor: 'white', py: 8 }}>
        <Container maxWidth="lg">
          <Grid container spacing={4}>
            {stats.map((stat, index) => (
              <Grid item xs={6} md={3} key={index}>
                <Paper sx={{
                  p: 3,
                  textAlign: 'center',
                  transition: 'transform 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: `0 12px 24px ${alpha(colors.primary, 0.15)}`
                  }
                }}>
                  <Box sx={{
                    width: 60,
                    height: 60,
                    borderRadius: '50%',
                    background: alpha(colors.primary, 0.1),
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto 16px auto',
                    color: colors.primary
                  }}>
                    {stat.icon}
                  </Box>
                  <Typography variant="h3" sx={{ fontWeight: 700, color: colors.primary }}>
                    {stat.value}
                  </Typography>
                  <Typography variant="body2" sx={{ color: colors.textSecondary }}>
                    {stat.label}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* CTA Section */}
      <Box sx={{ py: 8, background: colors.gradient }}>
        <Container maxWidth="lg">
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              justifyContent: "center",
              alignItems: "center",
              gap: { xs: 2, sm: 4 },
              mt: 4,
              px: 2,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Email sx={{ color: "#ffffff" }} />
              <Typography sx={{ color: "#ffffff" }}>
                lidertechnologysolutions@gmail.com
              </Typography>
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Phone sx={{ color: "#ffffff" }} />
              <Typography sx={{ color: "#ffffff" }}>
                +91 98403 69993
              </Typography>
            </Box>
          </Box>
        </Container>
      </Box>

      <HomeFooter />
    </Box>
  );
};

export default RDTestingLab;